import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { DevServerControl } from '../components/siteMap/DevServerControl';
import { resolveSiteMapDevServer, SECTION_DEV_SERVER, type DevServerId } from '../lib/devServersRegistry';

export const BASE = import.meta.env.BASE_URL;
export const SITE_BUILDER_URL = `${BASE}site-configurator/`;

/** Local dev ports for sibling apps (not mounted on the main SPA). */
export const DEV_PORTS = {
  whatsnext: 'http://localhost:3201',
  leaderOnboarding: 'http://localhost:3202',
  parachute: 'http://localhost:3203',
  peakPrioritiesSite: 'http://localhost:4322',
  marketingSite: 'http://localhost:4321',
  docsSite: 'http://localhost:3000',
  siteBuilderDirect: 'http://localhost:5174',
  objectInspector: 'http://localhost:3400',
  orchestrationUi: 'http://localhost:3103',
  docsServer: 'http://localhost:4008',
  peakLlm: 'http://localhost:3004',
  claritas: 'http://localhost:3220',
  visualLanguageServer: 'http://localhost:3210',
} as const;

export type ColorKey = 'primary' | 'sage' | 'amber' | 'coral' | 'charcoal' | 'plum' | 'teal';

export type SiteMapEntry = {
  path: string;
  title: string;
  description: string;
  icon: LucideIcon;
  color: ColorKey;
  external?: boolean;
  /** Repo-only static asset — shown for orientation, not a live link */
  repoOnly?: boolean;
  /** Override inferred dev server for status / start control */
  devServerId?: DevServerId;
};

export type SiteMapSection = {
  category: string;
  description: string;
  pages: SiteMapEntry[];
};

const colorClasses: Record<ColorKey, string> = {
  primary: 'bg-consulting-slate-blue-50 border-consulting-slate-blue-200 text-consulting-slate-blue-800',
  sage: 'bg-sage-50 border-sage-200 text-sage-800',
  amber: 'bg-[var(--mustard-dim)] border-[var(--mustard)] text-[var(--mustard)]',
  coral: 'bg-coral-50 border-coral-200 text-coral-800',
  charcoal: 'bg-charcoal-50 border-charcoal-200 text-charcoal-800',
  plum: 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]',
  teal: 'bg-[var(--green-dim)] border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)]',
};

const iconColorClasses: Record<ColorKey, string> = {
  primary: 'text-consulting-slate-blue-600',
  sage: 'text-sage-600',
  amber: 'text-[var(--mustard)]',
  coral: 'text-coral-600',
  charcoal: 'text-charcoal-600',
  plum: 'text-[var(--text-ter)]',
  teal: 'text-[var(--green)] dark:text-[var(--text-ter)]',
};

export function SiteMapCard({ page }: { page: SiteMapEntry }) {
  const IconComponent = page.icon;
  const devServerId = resolveSiteMapDevServer(page);
  const cardInner = (
    <div className="flex items-start gap-3">
      <div className={`p-2  ${colorClasses[page.color]}`}>
        <IconComponent className={`h-5 w-5 ${iconColorClasses[page.color]}`} />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-charcoal-900 group-hover:text-consulting-slate-blue-700 transition-colors mb-1 flex items-center gap-1.5">
          {devServerId && <DevServerControl serverId={devServerId} variant="block" />}
          {page.title}
          {page.external && <ExternalLink className="h-3.5 w-3.5 text-charcoal-400 shrink-0" />}
          {page.repoOnly && (
            <span className="text-[10px] uppercase tracking-wide text-charcoal-400 font-sans font-medium">
              repo
            </span>
          )}
        </h3>
        <p className="text-sm text-charcoal-600 leading-relaxed">{page.description}</p>
        <div className="mt-2 flex items-center text-xs text-charcoal-500 font-mono truncate">
          <span className="truncate">{page.path}</span>
          {!page.repoOnly && (
            <ArrowRight className="h-3 w-3 ml-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
          )}
        </div>
      </div>
    </div>
  );

  const className = page.repoOnly
    ? 'block p-4  border border-dashed border-charcoal-200 bg-charcoal-50/50'
    : 'group block p-4  border border-charcoal-200 hover:shadow-md transition-all duration-200 hover:border-charcoal-300';

  if (page.repoOnly) {
    return <div className={className}>{cardInner}</div>;
  }

  if (page.external || page.path.startsWith('http')) {
    return (
      <a href={page.path} target="_blank" rel="noopener noreferrer" className={className}>
        {cardInner}
      </a>
    );
  }

  return (
    <Link to={page.path} className={className}>
      {cardInner}
    </Link>
  );
}

export function SiteMapSectionBlock({ section }: { section: SiteMapSection }) {
  const sectionDevServerId = SECTION_DEV_SERVER[section.category];
  return (
    <div className="bg-[var(--bg-raised)] shadow-lg border border-charcoal-200 overflow-hidden">
      <div className="bg-gradient-to-r from-charcoal-50 to-sky-50 px-6 py-4 border-b border-charcoal-200">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-xl font-bold text-charcoal-900 mb-1">{section.category}</h2>
            <p className="text-charcoal-600">{section.description}</p>
          </div>
          {sectionDevServerId && (
            <DevServerControl serverId={sectionDevServerId} variant="block" />
          )}
        </div>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {section.pages.map((page) => (
            <SiteMapCard key={`${section.category}-${page.path}`} page={page} />
          ))}
        </div>
      </div>
    </div>
  );
}
